import { observer } from 'mobx-react-lite';
import { Fragment, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type DetailedValidationError,
  EmptyTable,
  Table,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
  useFieldsMap,
  useValidationErrors,
} from '@ifs-gcp/components';

import { useAuditStore } from '@/store/auditStore';
import { useRouterStore } from '@/store/routerStore';
import { useTouchedFieldsStore } from '@/store/validationStore';
import { getTranslation } from '@/utils/translations';

import { GenericErrorRow } from './error-rows';
import { ChecklistErrorRow } from './error-rows/checklist-error-row';

interface StatusTableProps {
  moduleId: string;
  variant?: 'primary' | 'secondary';
}

export const StatusTable = observer(({ moduleId, variant }: StatusTableProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const audit = useAuditStore((state) => state.audit)!;
  const scrollToElement = useRouterStore((state) => state.scrollToElement);
  const { touchAll } = useTouchedFieldsStore();
  const tableRef = useRef<HTMLTableElement>(null);
  const [fieldsMap] = useFieldsMap(audit);
  const groupedErrors = useValidationErrors(audit.status.sortedErrors, moduleId, fieldsMap);

  useEffect(() => {
    const firstRow = tableRef.current?.querySelector(
      '[data-focusable]',
    ) as HTMLTableRowElement | null;
    firstRow?.focus();
  }, []);

  useEffect(() => {
    touchAll(
      audit.status.sortedErrors.map((error) => error.field.props.id),
      Array.from(audit.modules.modules.keys()),
    );
  }, [audit, touchAll]);

  const redirectHandler = (errorId: string, paths: DetailedValidationError['paths']) => {
    scrollToElement(errorId, paths[0].props.id, paths[1].props.id, paths?.[2]?.props?.id);
  };

  return (
    <Table
      stickyHeader
      ref={tableRef}
      variant={variant}
      head={
        <TableHead>
          <TableHeadCell width={130}>{t('audit.status.type')}</TableHeadCell>
          <TableHeadCell>{t('audit.status.name')}</TableHeadCell>
          <TableHeadCell>{t('audit.status.note')}</TableHeadCell>
        </TableHead>
      }
    >
      {Object.keys(groupedErrors).length ? (
        Object.values(groupedErrors).map((errorGroup, index) => {
          const label = getTranslation(errorGroup.label, language);

          return (
            <Fragment key={label}>
              <TableRow hover>
                <TableCell colSpan={3}>{label}</TableCell>
              </TableRow>

              {errorGroup.errors.map((error, errorIndex) => {
                const params = { error, errorIndex, language, moduleId, redirectHandler };

                if (error.type === 'checklist') {
                  return <ChecklistErrorRow {...params} key={`${index}-${errorIndex}`} />;
                }

                return <GenericErrorRow {...params} key={`${index}-${errorIndex}`} />;
              })}
            </Fragment>
          );
        })
      ) : (
        <EmptyTable colSpan={3}>{t('audit.status.no_invalid_data')}</EmptyTable>
      )}
    </Table>
  );
});
